import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { orderRequest } from '../redux/slices/orderSlice';
import { FormData, OrderType } from './useCheckoutForm';
import { buildOrderPayload, type OrderPayload } from '../utils/orderPayloadBuilder';

export const useOrder = () => {
  const dispatch = useAppDispatch();
  
  // Get all required data from Redux
  const { items: cartItems } = useAppSelector((state) => state.cart);
  const { selectedRestaurant } = useAppSelector((state) => state.restaurant);
  const modifiersState = useAppSelector((state) => state.modifiers);
  const { items: menuItems } = useAppSelector((state) => state.menuItems);
  const menuItemDetails = useAppSelector((state) => state.menuItemDetails);
  const { loading: orderLoading,error: orderError, orderResponse } = useAppSelector((state) => state.order);

  // Payment mode state
  const [paymentMode, setPaymentMode] = useState<'pay_now' | 'pay_later'>('pay_now');
  
  // Popup states
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  /**
   * Prepare order payload using shared utility
   */
  const preparePayload = useCallback((
    formData: FormData,
    orderType: OrderType,
    paymentMode: 'pay_now' | 'pay_later'
  ): OrderPayload => {
    return buildOrderPayload(
      formData,
      orderType === 'pickup' ? 'pickup' : 'delivery',
      paymentMode,
      cartItems,
      selectedRestaurant,
      modifiersState,
      menuItems,
      menuItemDetails
    );
  }, [cartItems, selectedRestaurant, modifiersState, menuItems, menuItemDetails]);

  /**
   * Handle placing order
   */
  const handleOrder = useCallback((
    formData: FormData,
    total: number,
    orderType: OrderType,
    mode: 'pay_now' | 'pay_later'
  ) => {
    setPaymentMode(mode);
    const completeOrderPayload = preparePayload(formData, orderType, mode);
    dispatch(orderRequest(completeOrderPayload));
  }, [dispatch, preparePayload]);

  /**
   * Handle order response based on payment mode
   */
  useEffect(() => {
    if (orderResponse) {
      if (paymentMode === 'pay_now') {
        // Pay Now: Redirect to payment link
        const paymentLink = orderResponse.paymentLink || orderResponse.payment_link;
        
        if (paymentLink) {
          // Simple redirect - QuanticPaymentCallback will handle success/failure
          window.location.href = paymentLink;
        }
      } else {
        // Pay Later: Show success popup only (cart clearing handled by popup button)
        setShowSuccessPopup(true);
      }
    }
  }, [orderResponse, paymentMode, dispatch]);

  /**
   * Handle order error
   */
  useEffect(() => {
    if (orderError) {
      setShowErrorPopup(true);
    }
  }, [orderError]);

  /**
   * Handle payment success
   */
  const handlePaymentSuccess = useCallback((onClose?: () => void) => {
    setShowSuccessPopup(false);
    if (onClose) {
      onClose();
    }
  }, []);

  /**
   * Handle error popup close
   */
  const handleErrorClose = useCallback(() => {
    setShowErrorPopup(false);
  }, []);

  return {
    // State
    paymentMode,
    setPaymentMode,
    orderLoading,
    orderError,
    showSuccessPopup,
    showErrorPopup,
    orderResponse,
    
    // Handlers
    handleOrder,
    handlePaymentSuccess,
    handleErrorClose,
  };
};
